import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Calendar, 
  Mail, 
  Phone, 
  User, 
  Home,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Search,
  Filter,
  UserPlus,
  FileText,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editNotes, setEditNotes] = useState('');

  const stats = {
    total: inquiries.length,
    contact: inquiries.filter(i => i.type === 'contact').length,
    schedule: inquiries.filter(i => i.type === 'schedule').length,
    new: inquiries.filter(i => i.status === 'new' || i.status === 'pending').length,
    unassigned: inquiries.filter(i => !i.assigned_to).length,
    resolved: inquiries.filter(i => i.status === 'resolved' || i.status === 'confirmed').length
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [inquiriesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/inquiries/`),
        axios.get(`${API}/users/employees`)
      ]);
      setInquiries(inquiriesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      // Get inquiry to check type before updating
      const inquiry = inquiries.find(i => i.id === inquiryId);
      
      await axios.put(`${API}/inquiries/${inquiryId}/status?status=${newStatus}`);
      
      // Show appropriate success message
      if (newStatus === 'confirmed' && inquiry?.type === 'schedule') {
        toast.success('Inquiry confirmed! Visit has been auto-scheduled.');
      } else {
        toast.success('Status updated successfully');
      }
      
      fetchData();
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry({ ...selectedInquiry, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const assignInquiry = async (inquiryId, employeeId) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}`, { assigned_to: employeeId });
      toast.success('Inquiry assigned successfully');
      fetchData();
      if (selectedInquiry?.id === inquiryId) {
        const emp = employees.find(e => e.id === employeeId);
        setSelectedInquiry({ ...selectedInquiry, assigned_to: employeeId, assigned_to_name: emp?.name });
      }
    } catch (error) {
      toast.error('Failed to assign inquiry');
    }
  };

  const saveNotes = async (inquiryId) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}`, { notes: editNotes });
      toast.success('Notes saved');
      fetchData();
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry({ ...selectedInquiry, notes: editNotes });
      }
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const getTimeSince = (dateString) => {
    const now = new Date();
    const created = new Date(dateString);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-500 text-white',
      pending: 'bg-yellow-500 text-white',
      contacted: 'bg-purple-500 text-white',
      confirmed: 'bg-green-600 text-white',
      resolved: 'bg-green-600 text-white',
      cancelled: 'bg-red-500 text-white'
    };
    return styles[status] || 'bg-gray-500 text-white';
  };

  const getTypeBadge = (type) => {
    if (type === 'contact') {
      return 'bg-brand-brown text-white';
    }
    return 'bg-brand-gold text-brand-brown';
  };

  const filteredInquiries = inquiries.filter(inquiry => {
    // Tab filter
    if (activeTab === 'contact' && inquiry.type !== 'contact') return false;
    if (activeTab === 'schedule' && inquiry.type !== 'schedule') return false;
    
    // Status filter
    if (statusFilter !== 'all' && inquiry.status !== statusFilter) return false;
    
    // Assigned filter
    if (assignedFilter === 'unassigned' && inquiry.assigned_to) return false;
    if (assignedFilter !== 'all' && assignedFilter !== 'unassigned' && inquiry.assigned_to !== assignedFilter) return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        inquiry.name?.toLowerCase().includes(search) ||
        inquiry.email?.toLowerCase().includes(search) ||
        inquiry.listing_title?.toLowerCase().includes(search) ||
        inquiry.message?.toLowerCase().includes(search)
      );
    }
    
    return true;
  });

  const viewDetail = (inquiry) => {
    setSelectedInquiry(inquiry);
    setEditNotes(inquiry.notes || '');
    setDetailDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
            Inquiry Tracking
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Track, assign, and manage all customer inquiries
          </p>
        </div>
        <Button 
          onClick={fetchData} 
          variant="outline" 
          className="rounded-none border-2"
          data-testid="refresh-inquiries-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold" data-testid="stat-total-inquiries">
              {stats.total}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Contact
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold" data-testid="stat-contact">
              {stats.contact}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold" data-testid="stat-schedule">
              {stats.schedule}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Clock className="w-4 h-4" />
              New/Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold text-yellow-600" data-testid="stat-new">
              {stats.new}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Unassigned
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold text-red-500" data-testid="stat-unassigned">
              {stats.unassigned}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Resolved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-heading text-3xl font-bold text-green-600" data-testid="stat-resolved">
              {stats.resolved}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or property..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-none border-2 pl-10"
            data-testid="search-inquiries"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none border-2 w-full sm:w-40" data-testid="filter-status">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={assignedFilter} onValueChange={setAssignedFilter}>
          <SelectTrigger className="rounded-none border-2 w-full sm:w-44" data-testid="filter-assigned">
            <UserPlus className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Assigned To" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignments</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {employees.map((emp) => (
              <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs and Table */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-secondary rounded-none p-1">
          <TabsTrigger value="all" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-all">
            All ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="contact" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-contact">
            <Mail className="w-4 h-4 mr-2" />
            Contact ({stats.contact})
          </TabsTrigger>
          <TabsTrigger value="schedule" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-schedule">
            <Calendar className="w-4 h-4 mr-2" />
            Schedule ({stats.schedule})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-0">
          <Card className="rounded-none border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead className="font-heading text-xs uppercase tracking-widest">Type</TableHead>
                    <TableHead className="font-heading text-xs uppercase tracking-widest">Contact</TableHead>
                    <TableHead className="font-heading text-xs uppercase tracking-widest">Property</TableHead>
                    <TableHead className="font-heading text-xs uppercase tracking-widest">Assigned To</TableHead>
                    <TableHead className="font-heading text-xs uppercase tracking-widest">Received</TableHead>
                    <TableHead className="font-heading text-xs uppercase tracking-widest">Status</TableHead>
                    <TableHead className="font-heading text-xs uppercase tracking-widest text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <div className="w-8 h-8 border-4 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
                      </TableCell>
                    </TableRow>
                  ) : filteredInquiries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                        <p className="font-body text-muted-foreground">No inquiries found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInquiries.map((inquiry) => (
                      <TableRow key={inquiry.id} className="hover:bg-secondary/30" data-testid={`inquiry-row-${inquiry.id}`}>
                        <TableCell>
                          <Badge className={`rounded-none font-heading text-[10px] uppercase tracking-widest ${getTypeBadge(inquiry.type)}`}>
                            {inquiry.type === 'contact' ? (
                              <><Mail className="w-3 h-3 mr-1" /> Contact</>
                            ) : (
                              <><Calendar className="w-3 h-3 mr-1" /> Schedule</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-heading font-semibold text-sm flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {inquiry.name}
                            </p>
                            <p className="font-body text-xs text-muted-foreground flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {inquiry.email}
                            </p>
                            {inquiry.phone && (
                              <p className="font-body text-xs text-muted-foreground flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {inquiry.phone}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {inquiry.listing_title ? (
                            <p className="font-body text-sm flex items-center gap-1">
                              <Home className="w-3 h-3 text-brand-gold" />
                              <span className="truncate max-w-[120px]">{inquiry.listing_title}</span>
                            </p>
                          ) : (
                            <span className="text-muted-foreground text-sm">General</span>
                          )}
                          {inquiry.preferred_date && (
                            <p className="font-body text-xs text-muted-foreground mt-1">
                              {inquiry.preferred_date} @ {inquiry.preferred_time}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={inquiry.assigned_to || "unassigned"} 
                            onValueChange={(value) => assignInquiry(inquiry.id, value === "unassigned" ? null : value)}
                          >
                            <SelectTrigger className="rounded-none border h-8 w-32" data-testid={`assign-select-${inquiry.id}`}>
                              {inquiry.assigned_to_name ? (
                                <span className="text-sm truncate">{inquiry.assigned_to_name}</span>
                              ) : (
                                <span className="text-sm text-red-500">Unassigned</span>
                              )}
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {employees.map((emp) => (
                                <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-body text-sm font-medium">{getTimeSince(inquiry.created_at)}</p>
                            <p className="font-body text-xs text-muted-foreground">{formatDate(inquiry.created_at)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={inquiry.status} 
                            onValueChange={(value) => updateStatus(inquiry.id, value)}
                          >
                            <SelectTrigger className="rounded-none border h-8 w-28" data-testid={`status-select-${inquiry.id}`}>
                              <Badge className={`rounded-none text-[9px] uppercase ${getStatusBadge(inquiry.status)}`}>
                                {inquiry.status}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => viewDetail(inquiry)}
                            className="rounded-none"
                            data-testid={`view-inquiry-${inquiry.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg" aria-describedby="inquiry-detail-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
              {selectedInquiry?.type === 'contact' ? (
                <><Mail className="w-5 h-5 text-brand-gold" /> Contact Inquiry</>
              ) : (
                <><Calendar className="w-5 h-5 text-brand-gold" /> Viewing Request</>
              )}
            </DialogTitle>
            <p id="inquiry-detail-description" className="sr-only">Details of the selected inquiry</p>
          </DialogHeader>
          
          {selectedInquiry && (
            <div className="space-y-6 mt-4">
              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Contact Information
                </h4>
                <div className="bg-secondary/30 p-4 space-y-2">
                  <p className="font-body flex items-center gap-2">
                    <User className="w-4 h-4 text-brand-gold" />
                    <span className="font-semibold">{selectedInquiry.name}</span>
                  </p>
                  <p className="font-body flex items-center gap-2">
                    <Mail className="w-4 h-4 text-brand-gold" />
                    <a href={`mailto:${selectedInquiry.email}`} className="text-blue-600 hover:underline">
                      {selectedInquiry.email}
                    </a>
                  </p>
                  {selectedInquiry.phone && (
                    <p className="font-body flex items-center gap-2">
                      <Phone className="w-4 h-4 text-brand-gold" />
                      <a href={`tel:${selectedInquiry.phone}`} className="text-blue-600 hover:underline">
                        {selectedInquiry.phone}
                      </a>
                    </p>
                  )}
                </div>
              </div>

              {/* Property */}
              {selectedInquiry.listing_title && (
                <div className="space-y-3">
                  <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Property of Interest
                  </h4>
                  <div className="bg-secondary/30 p-4">
                    <p className="font-body flex items-center gap-2">
                      <Home className="w-4 h-4 text-brand-gold" />
                      <span className="font-semibold">{selectedInquiry.listing_title}</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Preferred Date/Time for Schedule */}
              {selectedInquiry.type === 'schedule' && selectedInquiry.preferred_date && (
                <div className="space-y-3">
                  <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Preferred Viewing Time
                  </h4>
                  <div className="bg-secondary/30 p-4">
                    <p className="font-body flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-brand-gold" />
                      <span>{selectedInquiry.preferred_date}</span>
                      <Clock className="w-4 h-4 text-brand-gold ml-2" />
                      <span>{selectedInquiry.preferred_time}</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Message */}
              {selectedInquiry.message && (
                <div className="space-y-3">
                  <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Message
                  </h4>
                  <div className="bg-secondary/30 p-4">
                    <p className="font-body text-sm whitespace-pre-wrap">{selectedInquiry.message}</p>
                  </div>
                </div>
              )}

              {/* Assignment */}
              <div className="space-y-3">
                <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Assign To Employee
                </h4>
                <Select 
                  value={selectedInquiry.assigned_to || "unassigned"} 
                  onValueChange={(value) => assignInquiry(selectedInquiry.id, value === "unassigned" ? null : value)}
                >
                  <SelectTrigger className="rounded-none border-2" data-testid="dialog-assign-select">
                    <SelectValue placeholder="Select employee..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-3">
                <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Internal Notes
                </h4>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add notes about this inquiry (e.g., follow-up calls, customer preferences)..."
                  rows={3}
                  className="rounded-none border-2"
                  data-testid="inquiry-notes"
                />
                <Button 
                  onClick={() => saveNotes(selectedInquiry.id)}
                  variant="outline"
                  size="sm"
                  className="rounded-none border-2"
                  data-testid="save-notes-btn"
                >
                  Save Notes
                </Button>
              </div>

              {/* Status & Timeline */}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                      Status:
                    </span>
                    <Select 
                      value={selectedInquiry.status} 
                      onValueChange={(value) => updateStatus(selectedInquiry.id, value)}
                    >
                      <SelectTrigger className="rounded-none border h-8 w-32">
                        <Badge className={`rounded-none text-[9px] uppercase ${getStatusBadge(selectedInquiry.status)}`}>
                          {selectedInquiry.status}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-body text-xs text-muted-foreground">
                    Received {getTimeSince(selectedInquiry.created_at)}
                  </p>
                  <p className="font-body text-[10px] text-muted-foreground">
                    {formatDate(selectedInquiry.created_at)}
                  </p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-3">
                <Button 
                  asChild
                  variant="outline"
                  className="flex-1 rounded-none border-2"
                >
                  <a href={`mailto:${selectedInquiry.email}?subject=Re: Your inquiry about ${selectedInquiry.listing_title || 'our properties'}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </a>
                </Button>
                {selectedInquiry.phone && (
                  <Button 
                    asChild
                    variant="outline"
                    className="flex-1 rounded-none border-2"
                  >
                    <a href={`tel:${selectedInquiry.phone}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </a>
                  </Button>
                )}
                <Button 
                  onClick={() => {
                    updateStatus(selectedInquiry.id, selectedInquiry.type === 'schedule' ? 'confirmed' : 'resolved');
                    setDetailDialogOpen(false);
                  }}
                  className="flex-1 rounded-none bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
                  data-testid="mark-resolved-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {selectedInquiry.type === 'schedule' ? 'Confirm' : 'Resolve'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInquiries;
